import { APP_INITIALIZER, inject, Injectable, Injector, Provider } from "@angular/core";
import { Router } from "@angular/router";
import { NativePurchases } from "@capgo/native-purchases";

import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { AppleConfigService } from "@bitwarden/web-vault/app/services/api/apple-config.service";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";

/** What gloss-api's offer-code verify reports (OfferCodeConnector.manageOfferCodeSubscriptions). */
type VerifyResult = "credited" | "ignored" | "duplicate" | "pending";

/**
 * When to ask again about a redemption the server reported "pending" (gloss-api#404): Apple's
 * notification is crediting it at the same time, which usually takes seconds. Backs off, then
 * gives up for this session; it shows at a later launch.
 */
export const PENDING_RECHECK_DELAYS_MS = [5000, 10000, 20000];

/**
 * Native side of the iOS points-to-offer-code redemption (#5928). The user finishes it in the App
 * Store, often after leaving Credits, so it can't live in PointsRedemptionComponent: that is
 * rebuilt on every Credits visit and Cash Back tab switch, and each instance used to add its own
 * listeners and never remove them. Root-provided, so its listener is registered once; and
 * created at startup (offerCodeRedemptionInitializer), not on the first Credits visit: the native
 * plugin hands each StoreKit transaction to whichever listener exists when it arrives and drops it
 * otherwise, and iOS often restarts the app while the user is in the App Store (#6045).
 */
@Injectable({ providedIn: "root" })
export class OfferCodeRedemptionService {
  // Fetched when a transaction arrives, not at startup: AppleConfigService configures RevenueCat
  // as it's created, which a Credits or plans visit did before.
  private injector = inject(Injector);
  private notifyService = inject(ConfirmationDialogService);
  private router = inject(Router);
  /** Transactions waiting on a "pending" redemption, so one delivered again meanwhile isn't too. */
  private awaitingCredit = new Set<string>();

  constructor() {
    void NativePurchases.addListener("transactionUpdated", (transaction: any) =>
      this.onTransactionUpdated(transaction),
    );
  }

  // A redemption is applied here, and the app reloads to show it. Nothing on resume: the app can't
  // tell from there whether the user redeemed anything (#6046).
  private async onTransactionUpdated(transaction: any): Promise<void> {
    let result: VerifyResult | undefined;
    try {
      // Every StoreKit transaction comes here, renewals at each launch too: the server credits only
      // an offer-code redemption, once (gloss-api#389), and nothing changed for the rest.
      result = await this.verify(transaction);
    } catch (e) {
      this.notifyService.notify(NotificationEnum.error, "error", "failedToRedeem");
      return;
    }
    // Only for a credit: anything else (ignored, a duplicate, an answer without a result) left
    // the subscription as it was, and reloading for it would repeat at every launch.
    const credited =
      result === "credited" || (result === "pending" && (await this.awaitCredit(transaction)));
    if (!credited) {
      return;
    }
    try {
      await this.router.navigate(["/"]);
      window.location.reload();
    } catch (e) {
      this.notifyService.notify(NotificationEnum.error, "error", "failedToRedeem");
    }
  }

  /**
   * Whether a "pending" redemption is credited by the time it asks again, backing off ("credited",
   * or "duplicate" once Apple's notification credited it meanwhile). Not reloading straight away:
   * that would show the old subscription. And no reload if it isn't credited by the last ask: the
   * transaction comes back at each launch, and a claim stuck "pending" (up to 10 minutes) would
   * reload the app every time; the user is told it's on its way instead. A failed ask is no failed
   * redemption (the notification is crediting it), so it asks again at the next step.
   */
  private async awaitCredit(transaction: { transactionId: string }): Promise<boolean> {
    const id = transaction.transactionId;
    if (this.awaitingCredit.has(id)) {
      return false;
    }
    this.awaitingCredit.add(id);
    try {
      for (const delay of PENDING_RECHECK_DELAYS_MS) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        let result: VerifyResult | undefined;
        try {
          result = await this.verify(transaction);
        } catch (e) {
          continue;
        }
        if (result === "credited" || result === "duplicate") {
          return true;
        }
        if (result !== "pending") {
          return false;
        }
      }
      this.notifyService.notify(
        NotificationEnum.info,
        "processing",
        "offerCodeRedemptionProcessing",
      );
      return false;
    } finally {
      this.awaitingCredit.delete(id);
    }
  }

  private async verify(transaction: any): Promise<VerifyResult | undefined> {
    const response = await this.injector
      .get(AppleConfigService)
      .verifyOfferCodeTransaction(transaction.jwsRepresentation);
    return response?.result;
  }
}

/** Creates OfferCodeRedemptionService, and so its listener, when the app starts (#6045). */
export const offerCodeRedemptionInitializer: Provider = {
  provide: APP_INITIALIZER,
  multi: true,
  useFactory: () => {
    inject(OfferCodeRedemptionService);
    return (): void => undefined;
  },
};
